import type { DefaultContext, OperationVariables } from "@apollo/client";
import { useLazyQuery as useApolloLazyQuery } from "@apollo/client/react";
import {
  type ForbiddenAwareOnError,
  withWrappedForbiddenOnError,
} from "./wrapForbiddenAwareOnError";

type ExtendLazyQueryOptions<
  TData,
  TVariables extends OperationVariables,
> = NonNullable<Parameters<typeof useApolloLazyQuery<TData, TVariables>>[1]> &
  Record<string, unknown> & {
    context?:
      | DefaultContext
      | ((hookContext: DefaultContext | undefined) => DefaultContext);
    onError?: ForbiddenAwareOnError;
  };

export function useLazyQuery<
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: Parameters<typeof useApolloLazyQuery<TData, TVariables>>[0],
  options?: ExtendLazyQueryOptions<TData, TVariables>
) {
  const resolvedOptions: Parameters<
    typeof useApolloLazyQuery<TData, TVariables>
  >[1] =
    options === undefined
      ? undefined
      : withWrappedForbiddenOnError(
          options,
          typeof options.context === "function" ? undefined : options.context
        );

  return useApolloLazyQuery(query, resolvedOptions);
}
